import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Autocomplete,
  Typography,
} from '@mui/material';
import { AppButton, AppTextField } from '../design-system';
import { type Tenant } from '../../services/ProviderTenantsService';
import { providerEnterpriseService } from '../../services/ProviderEnterpriseService';
import { providerTenantsService } from '../../services/ProviderTenantsService';
import type { EnterprisePackageData } from '@cnapse/shared/schemas.js';
import * as dt from '@cnapse/shared/data.types.js';

interface EnterprisePackageAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  packageKind?: dt.ENTERPRISE_PACKAGE_KIND;
  preselectedTenantId?: string;
  title?: string;
}

const KIND_LABELS: Record<dt.ENTERPRISE_PACKAGE_KIND, string> = {
  [dt.ENTERPRISE_PACKAGE_KIND.Pilot]: 'pilot',
  [dt.ENTERPRISE_PACKAGE_KIND.Annual]: 'annual',
  [dt.ENTERPRISE_PACKAGE_KIND.Amendment]: 'amendment',
};

const EnterprisePackageAssignmentDialog: React.FC<EnterprisePackageAssignmentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  packageKind,
  preselectedTenantId,
  title = 'Assign enterprise package',
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [packages, setPackages] = useState<EnterprisePackageData[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<EnterprisePackageData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    void loadData();
    // loadData is stable for this dialog lifecycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, packageKind, preselectedTenantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [tenantList, packageList] = await Promise.all([
        providerTenantsService.getTenants(),
        providerEnterpriseService.listEnterprisePackages({
          assignableOnly: true,
          active: true,
          kind: packageKind,
          tenantId: preselectedTenantId as dt.Uuid | undefined,
        }),
      ]);
      setTenants(tenantList);
      setPackages(
        packageKind
          ? packageList
          : packageList.filter((pkg) => pkg.kind !== dt.ENTERPRISE_PACKAGE_KIND.Amendment),
      );
      if (preselectedTenantId) {
        setSelectedTenant(tenantList.find((t) => t.id === preselectedTenantId) ?? null);
      } else {
        setSelectedTenant(null);
      }
      setSelectedPackage(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assignment data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !selectedTenant) {
      return;
    }
    void (async () => {
      try {
        const packageList = await providerEnterpriseService.listEnterprisePackages({
          assignableOnly: true,
          active: true,
          kind: packageKind,
          tenantId: selectedTenant.id as dt.Uuid,
        });
        setPackages(
        packageKind
          ? packageList
          : packageList.filter((pkg) => pkg.kind !== dt.ENTERPRISE_PACKAGE_KIND.Amendment),
      );
        setSelectedPackage((current) =>
          current && packageList.some((pkg) => pkg.packageId === current.packageId) ? current : null,
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load packages');
      }
    })();
  }, [open, selectedTenant, packageKind]);

  const handleSubmit = async () => {
    if (submitting) return;

    if (!selectedTenant || !selectedPackage) {
      setError('Select a tenant and package');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await providerEnterpriseService.assignEnterprisePackage({
        tenantId: selectedTenant.id as dt.Uuid,
        packageId: selectedPackage.packageId,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign package');
    } finally {
      setSubmitting(false);
    }
  };

  const formatPackageLabel = (pkg: EnterprisePackageData): string => {
    const kind = KIND_LABELS[pkg.kind as dt.ENTERPRISE_PACKAGE_KIND] ?? pkg.kind;
    const tenantSuffix = pkg.tenantName ? ` · ${pkg.tenantName}` : '';
    return `${pkg.name} (${kind})${tenantSuffix}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <Typography variant="body2" color="text.secondary">
              Packages are defined on Products &amp; Pricing (Enterprise tab). Assignment applies the
              package limits and dates without overrides.
            </Typography>

            <Autocomplete
              options={tenants}
              getOptionLabel={(t) => t.name}
              value={selectedTenant}
              onChange={(_, value) => setSelectedTenant(value)}
              renderInput={(params) => <AppTextField {...params} label="Tenant" required />}
              disabled={!!preselectedTenantId}
            />

            <Autocomplete
              options={packages}
              getOptionLabel={formatPackageLabel}
              value={selectedPackage}
              onChange={(_, value) => setSelectedPackage(value)}
              renderInput={(params) => <AppTextField {...params} label="Enterprise package" required />}
              noOptionsText={
                packageKind
                  ? `No assignable ${KIND_LABELS[packageKind]} packages`
                  : 'No assignable packages'
              }
            />

            {selectedPackage && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>{selectedPackage.name}</strong>
                  {selectedPackage.internalNotes && (
                    <>
                      <br />
                      {selectedPackage.internalNotes}
                    </>
                  )}
                </Typography>
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <AppButton onClick={onClose} disabled={submitting}>
          Cancel
        </AppButton>
        <AppButton
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={submitting || loading || !selectedTenant || !selectedPackage}
        >
          {submitting ? 'Assigning…' : 'Assign package'}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default EnterprisePackageAssignmentDialog;
